import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Button, ButtonGroup, Form } from 'react-bootstrap';

// Configurar el worker de PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const SecurePDFViewer = ({ pdfUrl }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [lastValidPage, setLastValidPage] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1.0); // Estado para controlar el nivel de zoom interno

  // 1. Cargar el documento PDF una sola vez al montar o cambiar la URL
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setPageNum(1);
        setZoom(1.0);
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar el documento: ", error);
        setLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]);

  // 2. Renderizar con alta resolución basada en el ratio de píxeles de la pantalla
  const renderPage = useCallback(async (num) => {
    if (!pdfDoc) return;

    try {
      if(num === '' || typeof num !== 'number') return;

      const page = await pdfDoc.getPage(num);
      
      const containerWidth = containerRef.current ? containerRef.current.clientWidth : 600;
      const unscaledViewport = page.getViewport({ scale: 1.0 });
      
      /* INICIANDO PRUEBAS DE ZOOM  */

      //* Escala base adaptada al ancho multiplicada por el nivel de zoom actual
      const baseScale = (containerWidth - 32) / unscaledViewport.width;
      // const scale = (baseScale > 0 ? baseScale : 1) * currentZoom; // Opción para aplicar zoom, inhabilitada temporalmente
      const scale = (baseScale > 0 ? baseScale : 1);

      // Obtener la densidad de píxeles del dispositivo (ej: 2 o 3 en teléfonos modernos)
      const outputScale = window.devicePixelRatio || 1;

      // Viewport escalado para alta definición
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Ajustar resolución interna del canvas multiplicada por el devicePixelRatio
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      // Forzar mediante CSS que el canvas conserve el tamaño visual correcto en la interfaz
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';

      // Escalar el contexto para que PDF.js dibuje de forma nítida acorde a la alta densidad
      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        transform: transform,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error al renderizar la página:", error);
    }
  }, [pdfDoc]);

  // Actualizar el renderizado cada vez que cambie la página actual o el documento
  useEffect(() => {
    if (pdfDoc) {
      renderPage(pageNum);
    }
  }, [pdfDoc, pageNum, renderPage]);

  // Manejadores de navegación
  const goToPrevPage = () => {
    if (pageNum > 1) setPageNum(pageNum - 1);
  };

  const goToNextPage = () => {
    if (pageNum < numPages) setPageNum(pageNum + 1);
  };

  const goToSelectedPage = (event) => {
    const numTest = event.target.value;

    if(numTest === ''){
      setLastValidPage(pageNum)
      setPageNum('')
      return
    }

    const parsedNum = parseInt(numTest, 10)

    if(isNaN(parsedNum)) return;

    if(numTest > numPages){
      setPageNum(numPages)
    }

    if (parsedNum >= 1 && parsedNum <= numPages) {
      setPageNum(parsedNum)
    } 
  }

  const resetPage = () => {
    if(pageNum === '' || !pageNum) {
      setPageNum(lastValidPage)
    }
  }

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3.0));  // Zoom máximo de 3x
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.75)); // Zoom mínimo de 0.75x

  return (
    <div 
      ref={containerRef} 
      className='list-viewer-container'
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Barra de Controles de Paginación */}
      {!loading && numPages > 0 && (
        <div
          className='list-viewer-header'
        >
          <button 
            className='page-control back'
            onClick={goToPrevPage} 
            disabled={pageNum <= 1}
            style={{
              backgroundColor: pageNum <= 1 ? '#cbd5e1' : '#0284c7',
              cursor: pageNum <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            « Anterior
          </button>

          <div className='page-counter'>
            <span>Página</span>
            <Form.Control
              id='page-selector-input'
              className='page-selector'
              inputMode="numeric"
              value={pageNum}
              pattern='\d*'
              onChange={goToSelectedPage}
              onBlur={resetPage}
            />
            <span>de <b>{numPages}</b></span>
          </div>

          <button 
            className='page-control next'
            onClick={goToNextPage} 
            disabled={pageNum >= numPages}
            style={{
              backgroundColor: pageNum >= numPages ? '#cbd5e1' : '#0284c7',
              cursor: pageNum >= numPages ? 'not-allowed' : 'pointer',
            }}
          >
            Siguiente »
          </button>
        </div>
      )}

      {/* Contenedor del Visor (Canvas) */}
      <div
        className='list-viewer-body'
      >
        {loading && <><p style={{ padding: '40px', color: '#42546dff' }}>Cargando documento...</p></>}
        <canvas ref={canvasRef} className='list-viewer-canvas' />

        {
          (zoom < 0 && !true) &&
          <ButtonGroup className='zoom-control-group' aria-label="Zoom Page Control Group">
            <Button 
              variant="secondary"
              className='zoom-button'
              onClick={zoomIn}
            >
              Zoom In
            </Button>
            <Button 
              variant="secondary"
              className='zoom-button'
              onClick={zoomOut}
            >
              Zoom Out
            </Button>
          </ButtonGroup>
        }
      </div>
    </div>
  );
};

export { SecurePDFViewer };